import React, { useState, useEffect } from "react";
import { Grid } from "@material-ui/core";
import { Modal, Labelbox } from "../components";
import { INVOICE_ACTION } from "../redux/action";
import { Toaster } from "../utils/common";
import { getClientInvoiceId } from "../utils/invoiceIds";
import {
  getInvoicePaymentStatusLabel,
  getInvoiceCancellationComment,
  getInvoicePaidDateRaw,
  getInvoicePaymentHistory,
  paidDateToApiValue,
} from "../utils/invoiceStatusDisplay";
import { syncEfilingPaymentStatusFromInvoice } from "../utils/efilingApi";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import moment from "moment";

const PAYMENT_METHOD_IDS = [
  "Zelle",
  "Check",
  "ACH",
  "Authorize.Net",
  "QuickBooks",
  "none",
];

function getInitialPaymentStatus(invoice) {
  const explicit = String(
    invoice?.payment_status ?? invoice?.paymentStatus ?? "",
  ).trim();
  const validStatuses = ["Pending", "Partially Paid", "Paid", "Cancelled"];
  if (validStatuses.includes(explicit)) return explicit;
  return getInvoicePaymentStatusLabel(invoice);
}

function getInitialPaymentMethod(invoice) {
  const raw = String(
    invoice?.payment_method ??
      invoice?.paymentMethod ??
      invoice?.payment_mode ??
      invoice?.paymentMode ??
      "",
  ).trim();

  if (!raw) return "";
  if (PAYMENT_METHOD_IDS.includes(raw)) return raw;
  if (raw === "Not Yet paid") return "none";
  if (String(raw).toLowerCase() === "paper check") return "Check";
  return raw;
}

function emptyPaymentRow() {
  return {
    payment_date: moment().format("YYYY-MM-DD"),
    payment_method: "",
    amount: "",
    saved: false,
  };
}

function readExistingPayments(invoice) {
  return getInvoicePaymentHistory(invoice);
}

function buildInitialPaymentRows(invoice) {
  const existing = readExistingPayments(invoice);
  if (existing.length) {
    return existing.map((row) => ({
      payment_date:
        paidDateToApiValue(row.payment_date) || moment().format("YYYY-MM-DD"),
      payment_method: getInitialPaymentMethod({
        payment_method: row.payment_method,
      }),
      amount: Number(row.amount).toFixed(2),
      saved: true,
    }));
  }

  const amount = parseFloat(invoice?.amount_paid || 0);
  const date = paidDateToApiValue(getInvoicePaidDateRaw(invoice));
  const method = getInitialPaymentMethod(invoice);
  if (amount > 0 && (date || (method && method !== "none"))) {
    return [
      {
        payment_date: date || moment().format("YYYY-MM-DD"),
        payment_method: method && method !== "none" ? method : "",
        amount: amount.toFixed(2),
        saved: true,
      },
    ];
  }

  return [emptyPaymentRow()];
}

function sumUnsavedAmounts(rows) {
  return (rows || []).reduce((sum, row) => {
    if (row?.saved) return sum;
    return sum + (parseFloat(row?.amount) || 0);
  }, 0);
}

function getInitialAmountFields(invoice, status) {
  const total = parseFloat(invoice?.total || 0);
  const currentAmountPaid = parseFloat(invoice?.amount_paid || 0);
  const currentAmountDue = Math.max(total - currentAmountPaid, 0);

  if (status === "Paid") {
    return { amountPaid: total.toFixed(2), remainingTotal: "0.00" };
  }
  if (status === "Pending") {
    return { amountPaid: "0", remainingTotal: total.toFixed(2) };
  }
  if (status === "Partially Paid" || status === "Cancelled") {
    return {
      amountPaid: "",
      remainingTotal: currentAmountDue.toFixed(2),
    };
  }

  return {
    amountPaid: "",
    remainingTotal: currentAmountDue.toFixed(2),
  };
}

const PaymentStatusModal = ({ open, handleClose, invoice }) => {
  const [paymentStatus, setPaymentStatus] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [cancellationComment, setCancellationComment] = useState("");
  const [remainingTotal, setRemainingTotal] = useState("0.00");
  const [paidDate, setPaidDate] = useState("");
  const [newPayments, setNewPayments] = useState([emptyPaymentRow()]);

  const showPaidDateField =
    paymentStatus === "Paid" || paymentStatus === "Partially Paid";
  const recordedPaymentCount = getInvoicePaymentHistory(invoice).length;
  const showPaymentRows =
    paymentStatus === "Partially Paid" ||
    (paymentStatus === "Paid" && recordedPaymentCount > 0);

  useEffect(() => {
    if (!open || !invoice) return;

    const status = getInitialPaymentStatus(invoice);
    const method = getInitialPaymentMethod(invoice);
    const comment = getInvoiceCancellationComment(invoice);
    const { remainingTotal: initialRemaining } = getInitialAmountFields(
      invoice,
      status,
    );
    const existingPaidDate = paidDateToApiValue(getInvoicePaidDateRaw(invoice));

    setPaymentStatus(status);
    setPaymentMethod(status === "Cancelled" ? "" : method);
    setCancellationComment(comment);
    setRemainingTotal(initialRemaining);
    setPaidDate(
      status === "Paid" || status === "Partially Paid"
        ? existingPaidDate || moment().format("YYYY-MM-DD")
        : "",
    );
    setNewPayments(buildInitialPaymentRows(invoice));
  }, [open, invoice]);

  const handlePaymentStatusChange = (value) => {
    setPaymentStatus(value);
    const total = parseFloat(invoice?.total || 0);
    const currentAmountPaid = parseFloat(invoice?.amount_paid || 0);
    const currentAmountDue = Math.max(total - currentAmountPaid, 0);
    
    if (value === "Pending") {
      setRemainingTotal(total.toFixed(2));
      setPaidDate("");
    } else if (value === "Paid") {
      setRemainingTotal("0.00");
      setPaidDate((prev) => {
        if (prev) return prev;
        const existing = paidDateToApiValue(getInvoicePaidDateRaw(invoice));
        return existing || moment().format("YYYY-MM-DD");
      });
      setNewPayments((prev) => {
        if (!getInvoicePaymentHistory(invoice).length) return prev;
        const saved = (prev || []).filter((row) => row.saved);
        if (!saved.length) return prev;
        const savedTotal = saved.reduce(
          (sum, row) => sum + (parseFloat(row.amount) || 0),
          0,
        );
        const due = Math.max(total - savedTotal, 0);
        if (due <= 0.009) return saved;
        const draft = (prev || []).find((row) => !row.saved);
        return [
          ...saved,
          draft
            ? {
                ...draft,
                amount: String(draft.amount || "").trim()
                  ? draft.amount
                  : due.toFixed(2),
              }
            : {
                payment_date: moment().format("YYYY-MM-DD"),
                payment_method: "",
                amount: due.toFixed(2),
                saved: false,
              },
        ];
      });
    } else if (value === "Partially Paid") {
      setNewPayments(buildInitialPaymentRows(invoice));
      setRemainingTotal(currentAmountDue.toFixed(2));
      setPaidDate((prev) => {
        if (prev) return prev;
        const existing = paidDateToApiValue(getInvoicePaidDateRaw(invoice));
        return existing || moment().format("YYYY-MM-DD");
      });
    } else if (value === "Cancelled") {
      setPaymentMethod("");
      setPaidDate("");
      setRemainingTotal(currentAmountDue.toFixed(2));
    }
    if (value !== "Cancelled") {
      setCancellationComment("");
    }
  };

  const handleUpdate = async () => {
    // Validate required fields
    if (!paymentStatus) {
      Toaster.error("Please select a payment status");
      return;
    }
    
    if (paymentStatus === "Cancelled") {
      const comment = (cancellationComment || "").trim();
      if (!comment) {
        Toaster.error("Please enter a comment for the cancelled invoice");
        return;
      }
    } else if (
      paymentStatus !== "Partially Paid" &&
      !(paymentStatus === "Paid" && recordedPaymentCount > 0) &&
      !paymentMethod
    ) {
      Toaster.error("Please select a payment method");
      return;
    }

    // Calculate final values
    const total = parseFloat(invoice?.total || 0);
    const currentAmountPaid = parseFloat(invoice?.amount_paid || 0);
    let finalAmountPaid = 0;
    let finalRemainingTotal = total;
    let paymentsToSend = null;
    let partialStatus = paymentStatus;

    if (paymentStatus === "Cancelled") {
      finalAmountPaid = currentAmountPaid;
      finalRemainingTotal = Math.max(total - currentAmountPaid, 0);
    } else if (paymentStatus === "Pending") {
      finalAmountPaid = 0;
      finalRemainingTotal = total;
    } else if (paymentStatus === "Paid") {
      const rows = newPayments.filter(
        (row) =>
          !row.saved &&
          (String(row.amount || "").trim() !== "" ||
            String(row.payment_method || "").trim() !== ""),
      );
      if (rows.length) {
        for (const row of rows) {
          const amount = parseFloat(row.amount);
          if (!row.payment_date) {
            Toaster.error("Each payment needs a payment date");
            return;
          }
          if (!row.payment_method || row.payment_method === "none") {
            Toaster.error("Each payment needs a payment method");
            return;
          }
          if (!Number.isFinite(amount) || amount <= 0) {
            Toaster.error("Each payment needs an amount greater than 0");
            return;
          }
        }
        const added = rows.reduce((sum, row) => sum + parseFloat(row.amount), 0);
        finalAmountPaid = currentAmountPaid + added;
        if (Math.abs(finalAmountPaid - total) > 0.009) {
          Toaster.error(
            `Paid status requires the payments to equal the invoice total ($${total.toFixed(2)}).`,
          );
          return;
        }
        finalRemainingTotal = 0;
        paymentsToSend = rows.map((row) => ({
          payment_date: String(row.payment_date).slice(0, 10),
          payment_method: row.payment_method,
          amount: parseFloat(row.amount).toFixed(2),
        }));
      } else {
        finalAmountPaid = total;
        finalRemainingTotal = 0;
      }
    } else if (paymentStatus === "Partially Paid") {
      const rows = newPayments.filter(
        (row) =>
          !row.saved &&
          (String(row.amount || "").trim() !== "" ||
            String(row.payment_method || "").trim() !== ""),
      );
      if (!rows.length) {
        Toaster.error("Add at least one partial payment");
        return;
      }
      for (const row of rows) {
        const amount = parseFloat(row.amount);
        if (!row.payment_date) {
          Toaster.error("Each partial payment needs a payment date");
          return;
        }
        if (!row.payment_method || row.payment_method === "none") {
          Toaster.error("Each partial payment needs a payment method");
          return;
        }
        if (!Number.isFinite(amount) || amount <= 0) {
          Toaster.error("Each partial payment needs an amount greater than 0");
          return;
        }
      }
      const added = rows.reduce((sum, row) => sum + parseFloat(row.amount), 0);
      finalAmountPaid = currentAmountPaid + added;
      if (finalAmountPaid - total > 0.009) {
        Toaster.error(
          `Payments cannot exceed the invoice total ($${total.toFixed(2)}).`,
        );
        return;
      }
      finalRemainingTotal = Math.max(total - finalAmountPaid, 0);
      paymentsToSend = rows.map((row) => ({
        payment_date: String(row.payment_date).slice(0, 10),
        payment_method: row.payment_method,
        amount: parseFloat(row.amount).toFixed(2),
      }));
      partialStatus =
        finalRemainingTotal <= 0.009 ? "Paid" : "Partially Paid";
    }

    const clientInvoiceId = getClientInvoiceId(invoice);
    if (!clientInvoiceId) {
      Toaster.error(
        "Cannot update payment: invoice ID is missing. Please refresh and try again.",
      );
      return;
    }

    try {
      const updatePayload = {
        client_invoice_id: clientInvoiceId,
        payment_status: partialStatus,
        payment_method:
          paymentStatus === "Cancelled"
            ? null
            : paymentsToSend
              ? paymentsToSend[paymentsToSend.length - 1].payment_method
              : paymentMethod ||
                newPayments.filter((row) => row.saved).slice(-1)[0]
                  ?.payment_method ||
                null,
        amount_paid: finalAmountPaid.toFixed(2),
        remaining_total: finalRemainingTotal.toFixed(2),
        cancellation_comment:
          paymentStatus === "Cancelled"
            ? (cancellationComment || "").trim()
            : undefined,
      };

      if (paymentsToSend) {
        updatePayload.payments = paymentsToSend;
        updatePayload.paid_date =
          paymentsToSend[paymentsToSend.length - 1].payment_date;
      } else if (showPaidDateField) {
        const apiPaidDate = paidDateToApiValue(paidDate);
        if (apiPaidDate) {
          updatePayload.paid_date = apiPaidDate;
        }
      }

      const response = await INVOICE_ACTION.InvoicePaymentUpdate(updatePayload);
      
      if (response && response.status === 200) {
        if (partialStatus === "Paid") {
          try {
            await syncEfilingPaymentStatusFromInvoice({
              clientId:
                invoice?.billed_to ??
                invoice?.bill_to ??
                invoice?.client_id ??
                null,
              invoiceNo: invoice?.invoice_no ?? invoice?.invoiceNo ?? null,
              clientInvoiceId,
              paymentStatus: "Paid",
            });
          } catch (_e) {
            /* invoice update already succeeded; e-filing sync is best-effort */
          }
        }
        Toaster.success("Invoice Updated Successfully!");
        handleClose();
        // Refresh the page to show updated data
        window.location.reload();
      } else {
        Toaster.error("Error Updating Invoice!");
      }
    } catch (error) {
      console.error("Error updating invoice payment:", error);
      Toaster.error("Error Updating Invoice!");
    }
  };

  return (
    <Modal
      open={open}
      className="timesheet-view-modal"
      // title="Update Payment Status"
      handleClose={handleClose}
    >
      <Grid
        container
        spacing={3}
        style={{
          maxHeight: "450px",
          maxWidth: "760px",
          // overflowY: "auto",
        }}
      >
        <Grid item xs={12}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2 style={{ fontWeight: "bold" }}>Update Payment Status</h2>
            <button className="close-btn" onClick={handleClose}>
              ✕
            </button>
          </div>
          <div
            style={{
              fontSize: "15px",
              marginTop: "10px",
              marginBottom: "20px",
            }}
          >
            <span style={{ display: "block", marginBottom: "5px" }}>
              Invoice No:<strong> {invoice.invoice_no}</strong>
            </span>
            <span style={{ display: "block", marginBottom: "5px" }}>
              Total Amount:
              <strong> ${parseFloat(invoice?.total || 0).toFixed(2)}</strong>
            </span>
            <span style={{ display: "block", marginBottom: "5px" }}>
              Current Amount Paid:
              <strong>
                {" "}
                ${parseFloat(invoice?.amount_paid || 0).toFixed(2)}
              </strong>
            </span>
            <span style={{ display: "block", marginBottom: "5px" }}>
              Amount Due:<strong> ${remainingTotal}</strong>
            </span>
          </div>
          <Labelbox
            type="select"
            labelname="Payment Status"
            value={paymentStatus}
            dropdown={[
              { id: "Pending", value: "Pending" },
              { id: "Partially Paid", value: "Partially Paid" },
              { id: "Paid", value: "Paid" },
              { id: "Cancelled", value: "Cancelled" },
            ]}
            changeData={(value) => handlePaymentStatusChange(value)}
          />
        </Grid>

        {showPaymentRows && (
          <Grid item xs={12}>
            {newPayments.map((row, index) => (
              <div
                key={`new-payment-${index}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1fr 0.8fr auto",
                  gap: 8,
                  alignItems: "end",
                  marginBottom: 8,
                }}
              >
                <Labelbox
                  type="datepicker"
                  labelname="Payment date"
                  value={row.payment_date}
                  disabled={row.saved}
                  changeData={(value) => {
                    if (row.saved) return;
                    setNewPayments((prev) =>
                      prev.map((item, i) =>
                        i === index
                          ? { ...item, payment_date: value || "" }
                          : item,
                      ),
                    );
                  }}
                />
                {row.saved ? (
                  <Labelbox
                    type="text"
                    labelname="Payment method"
                    value={row.payment_method || ""}
                    readOnly
                  />
                ) : (
                  <Labelbox
                    type="select"
                    labelname="Payment method"
                    value={row.payment_method || ""}
                    dropdown={[
                      { id: "Zelle", value: "Zelle" },
                      { id: "Check", value: "Check" },
                      { id: "ACH", value: "ACH" },
                      { id: "Authorize.Net", value: "Authorize.Net" },
                      { id: "QuickBooks", value: "QuickBooks" },
                    ]}
                    changeData={(value) =>
                      setNewPayments((prev) =>
                        prev.map((item, i) =>
                          i === index
                            ? { ...item, payment_method: value }
                            : item,
                        ),
                      )
                    }
                  />
                )}
                <Labelbox
                  type="text"
                  labelname="Amount"
                  value={row.amount == null ? "" : String(row.amount)}
                  readOnly={row.saved}
                  changeData={(value) => {
                    if (row.saved) return;
                    const next = newPayments.map((item, i) =>
                      i === index ? { ...item, amount: value } : item,
                    );
                    setNewPayments(next);
                    const total = parseFloat(invoice?.total || 0);
                    const currentAmountPaid = parseFloat(
                      invoice?.amount_paid || 0,
                    );
                    setRemainingTotal(
                      Math.max(
                        total - currentAmountPaid - sumUnsavedAmounts(next),
                        0,
                      ).toFixed(2),
                    );
                  }}
                />
                {newPayments.length > 1 && !row.saved ? (
                  <button
                    type="button"
                    aria-label="Delete payment"
                    onClick={() => {
                      const next = newPayments.filter((_, i) => i !== index);
                      setNewPayments(next);
                      const total = parseFloat(invoice?.total || 0);
                      const currentAmountPaid = parseFloat(
                        invoice?.amount_paid || 0,
                      );
                      setRemainingTotal(
                        Math.max(
                          total - currentAmountPaid - sumUnsavedAmounts(next),
                          0,
                        ).toFixed(2),
                      );
                    }}
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: "8px 4px",
                      marginBottom: 4,
                    }}
                  >
                    <DeleteOutlineIcon
                      htmlColor="#d32f2f"
                      style={{ color: "#d32f2f", fontSize: 22 }}
                    />
                  </button>
                ) : (
                  <span />
                )}
              </div>
            ))}
            {paymentStatus === "Partially Paid" && (
              <button
                type="button"
                className="employee-btn-cancel"
                onClick={() =>
                  setNewPayments((prev) => [...prev, emptyPaymentRow()])
                }
              >
                Add another payment
              </button>
            )}
          </Grid>
        )}

        {showPaymentRows ? null : paymentStatus === "Cancelled" ? (
          <Grid item xs={12} className="payment-status-cancel-comment">
            <Labelbox
              type="textarea"
              labelname="Cancellation comment"
              value={cancellationComment}
              changeData={(value) => setCancellationComment(value)}
              rows={4}
            />
          </Grid>
        ) : (
          <>
            <Grid item xs={12}>
              <Labelbox
                type="select"
                labelname="Payment Method"
                value={paymentMethod}
                dropdown={[
                  { id: "Zelle", value: "Zelle" },
                  { id: "Check", value: "Check" },
                  { id: "ACH", value: "ACH" },
                  { id: "Authorize.Net", value: "Authorize.Net" },
                  { id: "QuickBooks", value: "QuickBooks" },
                  { id: "none", value: "Not Yet paid" },
                ]}
                changeData={(value) => setPaymentMethod(value)}
              />
            </Grid>
            {showPaidDateField && (
              <Grid item xs={12}>
                <Labelbox
                  type="datepicker"
                  labelname="Paid Date"
                  value={paidDate}
                  changeData={(value) => setPaidDate(value)}
                />
              </Grid>
            )}
          </>
        )}

        {/* <Grid item xs={12}>
          <p><strong>Due Amount: ${remainingTotal}</strong></p>
        </Grid> */}

        <Grid item style={{ width: "100%" }}>
          <div
            className=""
            style={{
              gap: "10px",
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <button
              style={{ width: "50%" }}
              className="employee-btn-cancel"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              style={{
                width: "50%",
                backgroundColor: "#0067b8",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                padding: "10px",
              }}
              onClick={handleUpdate}
            >
              Update
            </button>
            {/* <Button
              style={{ width: "50%" }}
              text="Update"
              handleClick={handleUpdate}
            /> */}
            {/* <Button text="Cancel" handleClick={handleClose} /> */}
          </div>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default PaymentStatusModal;
